/**
 * @file Common JavaScript - Global Logic Only
 * @description Contains only the JavaScript logic that is truly needed on ALL pages
 * This file should be kept minimal and only include essential global functionality
 */

import "./utils/safe-console.js";

// Import core modules that are needed globally
import { initTippyManager } from "./services/tippy-manager.js";

// Import shared modules that are needed globally
import { initUserManager } from "./shared/user/user-manager.js";
import { initApiService } from "./shared/api/api-service.js";
import { initUIComponents } from "./components/ui-components.js";

// Import legacy components that are needed globally
import { ShareLink } from "./components/ShareLink.js";
import { initAccountMenu } from "./components/account-menu.js";
import { initChatBubbles } from "./components/chat-bubbles.js";
import { initSliders } from "./components/sliders.js";
import { initRegion } from "./features/region/index.js";
import initFilterToggles from "./components/filters-ui.js";

// Import tab toggle utility for global use
import { autoInitTabToggles } from "./utils/tab-toggle.js";

let commonInitialized = false;

/**
 * Initialize common functionality that is needed on ALL pages
 * This should be minimal and only include truly global logic
 */
async function initCommon() {
  if (commonInitialized) {
    console.debug("Common: Already initialized, skipping...");
    return;
  }

  try {
    console.debug("Common: Initializing global functionality...");

    // Initialize Tippy Manager first for tooltip management (needed globally)
    initTippyManager();

    // Initialize shared modules that are needed globally
    await Promise.all([initUserManager(), initApiService(), initUIComponents()]);

    // Initialize legacy components that are needed globally
    await Promise.all([initAccountMenu(), initChatBubbles(), initSliders(), initRegion()]);

    // Initialize filter toggle behavior (delegated / safe on all pages)
    initFilterToggles();

    // Initialize tab toggles globally (auto-initializes containers with data-tab-container)
    autoInitTabToggles();

    // Initialize ShareLink component if needed
    if (typeof ShareLink !== "undefined") {
      new ShareLink();
    }

    commonInitialized = true;
    console.debug("Common: Global functionality initialized successfully");
  } catch (error) {
    console.error("Common: Initialization failed:", error);
    throw error;
  }
}

/**
 * Get common initialization status
 */
function getCommonStatus() {
  return {
    initialized: commonInitialized,
    type: "common",
  };
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCommon);
} else {
  initCommon();
}

// Export for global access
window.IPHUB_COMMON = {
  init: initCommon,
  getStatus: getCommonStatus,
};

// Export individual functions for module access
export { initCommon, getCommonStatus };
